import React, { useState, useEffect, useCallback, useMemo, memo } from 'react';
import { history } from 'umi';
import { Table, TableProps } from 'tea-component';
import { SortBy } from "tea-component/lib/table/addons";
import { getColumns } from './utils/common';
import { getNumberOfDotUnit } from '@gp/utils/common';
import classNames from 'classnames';
import './index.scss';
import { MENU_ID_MAP, SLASH_G_POP } from '@gp/constants/const';

const { autotip, sortable, pageable, scrollable } = Table.addons;

interface CostTablePropsType {
  // className?: string;
  // style?: React.CSSProperties;
  loading: boolean;
  data: any;
  setSortsParams: React.Dispatch<React.SetStateAction<SortBy<any>>> | null,
}

const DEFAULT_PROPS: CostTablePropsType = {
  loading: true,
  data: null,
  setSortsParams: null,
};

function CostTable(props: CostTablePropsType) {
  const {
    loading: propsLoading,
    data,
    setSortsParams,
  } = props;

  const [records, setRecords] = useState<any[]>([]);
  const [isClickSort, setIsClickSort] = useState<boolean>(false);
  const [sorts, setSorts] = useState<SortBy<any>>({by: '', order: 'desc'});
  const [paginationMsg, setPaginationMsg] = useState<{pageIndex: number, pageSize: number, total: number}>({pageIndex: 1, pageSize: 20, total: 0});
  const [loading, setLoading] = useState<boolean>(false);
  // 表格是否出现纵向滚动条
  const [tableIsScroll, setTableIsScroll] = useState<boolean>(false);

  // 是否没权限
  const isNoPermission = useMemo(() => {
    return data?.no_auth && !loading;
  }, [data?.no_auth, loading]);

  // 虚假的loading，目的是为了让横纵滚动条回到初始位置
  const resetScroll = useCallback(
    () => {
      setLoading(true);
      const t = setTimeout(() => {
        clearTimeout(t);
        setLoading(false);
      }, 50);
    },
    [],
  );

  /* useEffect(() => {
    setLoading(propsLoading);
  }, [propsLoading]); */

  // 初始化整理数据
  const orgRecords = useMemo(() => {
    const newRecords = data?.list?.map((item: any, index: number) => {
      return {
        uniqueId: item?.id + '_' + index,
        ...item,
      }
    }) || [];
    setPaginationMsg(Object.assign(paginationMsg, {total: newRecords?.length || 0}));

    if (!propsLoading) {
      setTimeout(() => {
        const tableBody = document.querySelector('.dbg-studio-overall-details-projects-dev-cost-table .tea-table__body');
        // @ts-ignore
        const flag = tableBody?.clientHeight < tableBody?.firstChild?.clientHeight;
        setTableIsScroll(flag);
      });
    }

    return newRecords;
  }, [propsLoading]);

  // 数据初始化及前端排序
  useEffect(() => {
    if (!Array.isArray(orgRecords) || !orgRecords?.length) {
      setRecords([]);
      return;
    }
    /* // 初始化数据没有排序
    if (!sorts?.by) {
      setRecords(orgRecords);
      return;
    }
    const sortRecords = getArraySort(
      orgRecords,
      sorts?.order || 'desc',
      'number',
      sorts?.by,
      true,
      'string',
      'project_name',
    );
    setRecords([...sortRecords]); */
    setRecords([...orgRecords]);
  }, [orgRecords]);

  const pageSizeOptions = [20, 50, 100];
  const showPageableOperation = paginationMsg?.total > Math.min(...pageSizeOptions);
  // 表格使用到的插件
  const addons = [
    scrollable({
      maxHeight: 500,
    }),
    pageable({
      stateTextVisible: true,
      pageSizeVisible: showPageableOperation,
      pageIndexVisible: showPageableOperation,
      jumpVisible: showPageableOperation,
      endJumpVisible: showPageableOperation,
      pageIndex: paginationMsg?.pageIndex,
      pageSize: paginationMsg?.pageSize,
      pageSizeOptions: [20, 50, 100],
      recordCount: paginationMsg?.total,
      stateText: `${getNumberOfDotUnit(paginationMsg?.total)} item${paginationMsg?.total > 1 ? 's' : ''} in total`,
      onPagingChange: ({pageIndex, pageSize}) => {
        setPaginationMsg({...paginationMsg, pageIndex: pageIndex as number, pageSize: pageSize as number});
        resetScroll();
      },
    }),
    sortable({
      columns: [
        'total_dev_budget',
        'total_spent_to_date',
        'total_spent_budget_percent',
        'dev_budget',
        'spent_to_date',
        'spent_budget_percent',
        'dev_cost',
      ],
      value: sorts ? [sorts] : [],
      onChange: (value) => {
        setIsClickSort(true);
        setSorts(value?.[0]);
        setSortsParams?.(value?.[0]);
        setPaginationMsg({...paginationMsg, pageIndex: 1});
        // resetScroll();
      },
    }),
    autotip({
      isLoading: propsLoading || loading,
      hideIcon: !(propsLoading || loading),  // loading状态显示图标，其他情况不显示
      emptyText: isNoPermission ? 'No permissions.' : 'The list has no data yet.',
      style:{
        height: 172,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        boxSizing: 'border-box',
      },
    }),
  ];

  const toDetails = useCallback((item: any, e: any, isJump = true) => {
    const { id: projectId, project_name: projectName } = item || {};
    if (!projectId) return '';
    const filter = encodeURIComponent(
      JSON.stringify({
        project: projectName || '',
        projectId: projectId || '',
        tab: 'finance',
        menuFrom: MENU_ID_MAP['pipeline_all-projects'], // 用于标识左侧菜单栏当前选中高亮
      }),
    );
    const url = `${SLASH_G_POP}/Pipeline/ProjectList/Detail?filter=${filter}`;
    if (isJump) {
      e?.preventDefault();
      e?.stopPropagation();
      history.push(url);
    }

    return url;
  }, []);

  // if (paginationMsg?.total <= 20) addons.shift();

  return (
    <Table
      className={classNames({
        'dbg-common-table dbg-studio-overall-details-projects-dev-cost-table sort-icon-color dbg-table-sticky dbh-action-state-center-table': true,
        'scroll-table-header': tableIsScroll,
      })}
      // verticalTop
      records={(loading || propsLoading || isNoPermission) ? [] : records}
      recordKey="uniqueId"
      columns={getColumns(
        data?.year,
        data?.month,
        toDetails,
      ) as TableProps['columns']}
      addons={addons}
    />
  );
}

CostTable.defaultProps = DEFAULT_PROPS;

export default memo(CostTable);
